import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/shared/ui/sheet";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { coachingService } from "../../api/coachingService";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CoachingSettingsDrawer({ open, onClose }: Props) {
  const { t } = useTranslation("coaching");
  const queryClient = useQueryClient();
  const {
    data: settings,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["coaching-settings"],
    queryFn: coachingService.getCoachingSettings,
    enabled: open,
    staleTime: 30_000,
    meta: { suppressGlobalErrorToast: true },
  });

  const [allowSelfInitiated, setAllowSelfInitiated] = useState(false);

  useEffect(() => {
    if (settings) setAllowSelfInitiated(settings.allowSelfInitiated);
  }, [settings]);

  const updateMutation = useMutationWithToast({
    mutationFn: () => coachingService.updateCoachingSettings({ allowSelfInitiated }),
    successToast: t("settings.toastSaved"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coaching-settings"] });
      onClose();
    },
  });

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t("settings.title")}</SheetTitle>
          <SheetDescription>{t("settings.description")}</SheetDescription>
        </SheetHeader>

        {isError || (!isLoading && !settings) ? (
          // audit-0713 C1c:拉取失败绝不渲染默认值表单 —— 管理员误存会用默认值覆盖真实配置
          <QueryErrorBanner isError className="mt-12" onRetry={() => void refetch()} />
        ) : isLoading ? (
          <div className="flex items-center justify-center h-40 mt-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 mt-6">
            <div className="rounded border p-3 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <Label className="text-base font-medium">
                    {t("settings.allowSelfInitiated")}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("settings.allowSelfInitiatedHint")}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={allowSelfInitiated}
                  onClick={() => setAllowSelfInitiated((p) => !p)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    allowSelfInitiated ? "bg-primary" : "bg-muted"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      allowSelfInitiated ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="text-xs text-muted-foreground">{t("settings.futureNote")}</div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>
                {t("settings.cancel")}
              </Button>
              <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : null}
                {t("settings.save")}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
